import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../utils/api';
import { MATRIX } from '../utils/roles';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    // If we just logged out, don't try to fetch again until page reload
    if (sessionStorage.getItem('mi_hrm_logout_pending')) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await authApi.me();
      if (data.success) {
        // Keep role override if set during session
        const savedRole = sessionStorage.getItem('mi_hrm_role_override');
        if (savedRole && data.data) {
          setUser({ ...data.data, role: savedRole, is_role_overridden: true });
        } else {
          setUser(data.data);
        }
      }
      else setUser(null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    fetchMe(); 
  }, [fetchMe]);

  const login = async (credentials) => {
    sessionStorage.removeItem('mi_hrm_logout_pending');
    sessionStorage.removeItem('mi_hrm_role_override');
    const { data } = await authApi.login(credentials);
    if (data.success) {
      if (data.access_token) sessionStorage.setItem('mi_hrm_token', data.access_token);
      setUser(data.data);
    }
    return data;
  };

  const logout = useCallback(async () => {
    // 1. Mark as logging out to prevent fetchMe loops
    sessionStorage.setItem('mi_hrm_logout_pending', '1');
    sessionStorage.removeItem('mi_hrm_token');
    sessionStorage.removeItem('mi_hrm_role_override');
    
    // 2. Clear local state immediately
    setUser(null);
    
    const isEss = window.location.pathname.startsWith('/ess');
    const isTimeout = sessionStorage.getItem('mi_hrm_logout_reason') === 'timeout';
    sessionStorage.removeItem('mi_hrm_logout_reason');
    
    // 3. Inform backend and wait briefly
    try { 
      await authApi.logout(); 
    } catch {}
    
    // 4. Force a clean redirect
    let redirectUrl = '/login';
    if (isTimeout) {
      redirectUrl += '?reason=timeout';
      if (isEss) {
        redirectUrl += '&redirect=/ess';
      }
    } else if (isEss) {
      redirectUrl += '?redirect=/ess';
    }
    window.location.href = redirectUrl;
  }, []);

  // Inactivity Timeout (5 minutes)
  useEffect(() => {
    if (!user) return;
    
    let timeout;
    let lastReset = 0;
    
    const resetTimer = () => {
      const now = Date.now();
      // Throttle: only reset timer at most once every 2 seconds to avoid layout/reflow overhead from mousemove/scroll
      if (now - lastReset > 2000) {
        lastReset = now;
        clearTimeout(timeout);
        timeout = setTimeout(() => { 
          sessionStorage.setItem('mi_hrm_logout_reason', 'timeout');
          logout(); 
        }, 5 * 60 * 1000); // 5 minutes
      }
    };

    const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    
    // Set initial timer immediately
    clearTimeout(timeout);
    timeout = setTimeout(() => { 
      sessionStorage.setItem('mi_hrm_logout_reason', 'timeout');
      logout(); 
    }, 5 * 60 * 1000);

    return () => {
      clearTimeout(timeout);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, logout]);

  const changeRole = (newRole) => {
    if (!user) return;
    sessionStorage.setItem('mi_hrm_role_override', newRole);
    setUser(prev => prev ? { ...prev, role: newRole, is_role_overridden: true } : null);
  };

  const can = (module, action) => {
    if (!user) return false;
    if (user.role === 'system_admin') return true; // Super admin override
    
    // Switch to matrix permissions if role is overridden or user doesn't have custom permissions list
    if (user.is_role_overridden || !user.permissions || user.permissions.length === 0) {
      const rolePerm = MATRIX[user.role]?.[module];
      if (rolePerm) {
        return rolePerm[action] === true;
      }
    }
    
    if (!user.permissions) return false;
    const p = user.permissions.find(item => item.module === module);
    if (!p) return false;
    return p[`can_${action}`] === true;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, changeRole, loading, login, logout, fetchMe, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
